import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input }from 'reactstrap';
import Body from '../Components/layout/Body';
import ReviewRow from '../Components/layout/ReviewRow';
import { ReviewRowIndependence } from './GatherInfoReview';
import { extractActivityButtons } from './ConstActivityBtns';
import { PLATFORM_BUTTONS } from './ConstPlatformBtns';
import { ENVIRONMENT_BUTTONS } from './ConstEnvironmentBtns';
import { POSITION_BUTTONS } from './ConstBodyPositionBtns';
import { IMPORTANCE_BUTTONS } from './ConstImportanceBtns';
import { CHECKLIST_BUTTONS } from './ConstChecklistBtns';
import { extractBodySiteButtons } from './ConstBodySiteBtns';
import { VISION_BUTTONS } from './ConstVisionBtns';
import { extractIssueButtons } from './ConstIssueBtns';
import { ValuesList } from '../Components/layout/ReviewRow';
import { TestDriveInfoTable, TestDriveTableRow } from '../Components/layout/DisplayTestDriveInfo';
import { UserTextOrNone } from '../Components/layout/DisplayUserText';
import { hasAValue } from '../App';
import { TEST_DRIVE_ACTIVITIES } from './ConstForTestDriveSection';

const REPORT_HELP = `
  <div>
    <p>The report includes:  
    </p>
    <ul>
      <li>All of the info you entered for the client’s profile (like goals & needs, as well as abilities)</li>
      <li>A summary of all Test-drives</li>
      <li>A summary of decision-making: this lists the Prospects still under consideration, as well as Picks that you’ve identified for next steps. </li>
    </ul>
    <p>In every section of the report, you can add a note, if you want. 
    </p>
    <p>You can use the Print feature in your browser to print the report, either to a PDF file or your printer. 
    </p>
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  providerName: PropTypes.string,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  providerName: ''
};

//content sub-component with unique content for this page
//TODO these components are redundant with GatherInfoReview code
function ReviewTabNextstepsReportContent(props) {
  const {
    firstName,
    providerName,
    reportData,
    summaryNotes,
    onChangeSummaryNotes,
    resetSummaryNotes,
    backgroundNotes,
    onChangeBackgroundNotes,
    resetBackgroundNotes,
    abilitiesNotes,
    onChangeAbilitiesNotes,
    resetAbilitiesNotes,
    testdrivesNotes,
    onChangeTestdrivesNotes,
    resetTestdrivesNotes,
    decisionsNotes,
    onChangeDecisionsNotes,
    resetDecisionsNotes,
    recommendationsNotes,
    onChangeRecommendationsNotes,
    resetRecommendationsNotes,
    saveFormData,
  } = props;

  return(
    <>
    {/* TODO include practitioner and date in header props */}
    <ReportHeader firstName={firstName} providerName={providerName}/>
    <ReportSectionHeader headerText="Summary" />
    <ReportSummary firstName={firstName} reportData={reportData}/>
    <NotesEditor 
      textValue={summaryNotes} 
      onChangeHandler={onChangeSummaryNotes} 
      resetNote={resetSummaryNotes}
      saveFormData={saveFormData}
    />

    <ReportSectionHeader headerText="Background Info" />
    <ReportGoalsAndNeeds
      activities={reportData.activities}
      priority={reportData.priority}
      platforms={reportData.platforms}
    />
    <ReportContexts
      environments={reportData.environments}
      bodyPositions={reportData.bodyPositions}
      indepSetup={reportData.indepSetup}
      indepUse={reportData.indepUse}
    />
    <ReportHistory
      hasCurrentMethod={reportData.hasCurrentMethod}
      currentMethods={reportData.currentMethods}
      pastMethods={reportData.pastMethods}
      platformsUsed={reportData.platformsUsed}
    />
    <NotesEditor 
      textValue={backgroundNotes} 
      onChangeHandler={onChangeBackgroundNotes} 
      resetNote={resetBackgroundNotes}
      saveFormData={saveFormData}
    />

    <ReportSectionHeader headerText="Key Abilities" />
    <ReportBodySites
      bodySites={reportData.bodySites}
      bodySiteComments={reportData.bodySiteComments}
    />
    <ReportVisualAbilities
      wearsGlasses={reportData.wearsGlasses}
      betterWithGlasses={reportData.betterWithGlasses}
      hasOtherVisualAids={reportData.hasOtherVisualAids}
      visualAidsNotes={reportData.visualAidsNotes}
      formalEvalNotes={reportData.formalEvalNotes}
      visionIssues={reportData.visionIssues}
      issuesNotes={reportData.issuesNotes}
    />
    <ReportLanguageCognition
      strengths={reportData.strengths}
      limitations={reportData.limitations}
    />
    <ReportOtherIssues
      otherIssues={reportData.otherIssues}
    />
    <NotesEditor 
      textValue={abilitiesNotes} 
      onChangeHandler={onChangeAbilitiesNotes} 
      resetNote={resetAbilitiesNotes}
      saveFormData={saveFormData}
    />

    <ReportSectionHeader headerText="All Test-drives" />
    <TestDriveInfoTable 
      testDrives={reportData.completedTestDrives} 
      showDetailsCol={true}
    />
    <p>View full detailed reports for each Test-drive at the end of this report.</p>
    <NotesEditor 
      textValue={testdrivesNotes} 
      onChangeHandler={onChangeTestdrivesNotes} 
      resetNote={resetTestdrivesNotes}
      saveFormData={saveFormData}
    />

    <ReportSectionHeader headerText="Decision-making" />
    <p>This section summarizes decisions about candidate access methods so far. Candidate access methods start out as Prospects. After test-driving, we select Picks, or access methods that merit further consideration, if there are any.</p>
    {( reportData.picks && reportData.picks.length > 0)
      ? <div>
        {reportData.picks.map((pick, ix) =>
          <ReportPickDecision key={ix} pick={pick} index={ix}/>
        )}
        </div>
    : <p>We haven't yet picked any access methods for further consideration.</p>
    }
    {( reportData.prospects && reportData.prospects.length > 0)
      ? <ReportProspects prospects={reportData.prospects} />
    : <p>There are no other access methods under consideration at this time.</p>
    }
    <NotesEditor 
      textValue={decisionsNotes} 
      onChangeHandler={onChangeDecisionsNotes} 
      resetNote={resetDecisionsNotes}
      saveFormData={saveFormData}
    />

    <ReportSectionHeader headerText="Recommendations" />
    {( reportData.picks && reportData.picks.length > 0)
      ? <div>
        <p>This section includes the next steps entered for each Pick, and any additional recommendations.</p>
        {reportData.picks.map((pick, ix) =>
          <ReportPickNextSteps key={ix} pick={pick} index={ix}/>
        )}
        </div>
    : <p>We haven't yet picked any access methods for further consideration.</p>
    }

    <NotesEditor 
      textValue={recommendationsNotes} 
      onChangeHandler={onChangeRecommendationsNotes} 
      resetNote={resetRecommendationsNotes}
      saveFormData={saveFormData}
    />

    <ReportSectionHeader headerText="Test-drive Reports (detailed)" />
    {( reportData.completedTestDrives && reportData.completedTestDrives.length > 0)
      ? <div>
        <p>This section provides more details about each Test-drive, including each activity performed, and measurements, if available.</p>
        {reportData.completedTestDrives.map((td, ix) =>
          <ReportTestDriveDetails key={ix} td={td} index={ix}/>
        )}
        </div>
    : <p>We haven't completed any Test-drives yet.</p>
    }
    <hr />
    </>
  )
}

function NotesEditor(props){
  const {
    textValue,
    onChangeHandler,
    resetNote,
    saveFormData
  } = props;

  //TODO text on report shows text entered in modal immediately as you type - OK?
  return(
    <Row className="aa-review-row">
      {(textValue) ?
        <p><strong>Notes: </strong>{textValue}</p>
        : <span></span>
      }
      <NotesModal 
        textValue={textValue} 
        onChangeHandler={onChangeHandler} 
        resetNote={resetNote}
        saveFormData={saveFormData}
      />
    </Row>
  );
}
function NotesModal(props) {
  const {
    textValue,
    onChangeHandler,
    resetNote,
    saveFormData
  } = props;

  const [modal, setModal] = useState(false);

  const toggle = () => setModal(!modal);
  const cancelAndClose = () => {
    resetNote();
    toggle();
  }
  const saveAndClose = () => {
    saveFormData();
    toggle();
  }

  let fieldValue = (textValue) ? textValue : "";
  //TODO btnName changes from Add to Edit immediately on first keypress - OK?
  let buttonName = (textValue) ? "Edit Note" : "Add Note";

  return (
    <div>
      <Button color="secondary" size="sm" onClick={toggle}>
        {buttonName}
      </Button>
      <Modal isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>Custom Note for the Report</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="custom-notes" className="aa-bold-text">
                Note to include in the report:
              </Label>
              <Input
                id="custom-notes"
                value={fieldValue}
                onChange={onChangeHandler}
                name="custom-notes"
                type="textarea"
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cancelAndClose}>
            Cancel
          </Button>{' '}
          <Button color="primary" onClick={saveAndClose}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

function ReportProspects(props){
  const prospects = props.prospects;

  const prospectNames = 
    prospects.map(p => getLabelForMethod(p));

  return(
    <Row className="aa-review-row border-top">
      <p><strong>Other Prospects</strong> still being considered:</p>
      <ValuesList values={prospectNames} lookup={null} skipWarning={true} />
    </Row>
  )
}

//Create a 'nice' label for an accessMethod (within the passed testDrive)
//returns a combo of setupName and accessMethodName, if both are available
//if no setupName, returns accessMethodName only (which could be empty, use UserTextOrNone in caller to replace with 'None entered')
function getLabelForMethod(td){
  return(
    td.setupName 
    ? td.setupName + (td.accessMethodName && " / " + td.accessMethodName) 
    : td.accessMethodName
  );
}

function ReportPickNextSteps(props){
  const pick = props.pick;
  const index = props.index+1;
  const nextSteps = pick.nextSteps;

  let heading = `Next steps for Pick ${index}.`;
  let spacer = " ";

  return(
    <Row className="aa-review-row border-top">
      <p>
        <strong>{heading}</strong>{spacer}
        <UserTextOrNone userText={getLabelForMethod(pick)} />
      </p>
      <p><UserTextOrNone userText={nextSteps}/></p>   
    </Row>
  )
}

function ReportPickDecision(props){
  const pick = props.pick;
  const index = props.index+1;
  const needsMet = pick.needsMet;
  const needsMetComments = pick.comments;

  let heading = `Pick ${index}.`;
  let spacer = " ";
  let periodSpace = ". ";
  let firstPickString = `We selected this access method for continued consideration.`;
  let secondPickString = 'This was based on Test-drive results as well as evaluation of the access method in meeting key needs.';

  //Refine secondPickString based on whether we actually have that info:
  let ratingsMade=true; //boolean for whether we have any results for this pick
  let someNeedsMet=true; //boolean for whether we've reviewed this pick relative to stated needs

  //TODO refine the condition for whether we have / don't have results
  if (!pick.clientRating && !pick.practitionerRating)
    ratingsMade = false;
  if (!pick.needsMet || pick.needsMet.length === 0)
    someNeedsMet = false;

  if (someNeedsMet && !ratingsMade){
    secondPickString = 'This was based on an evaluation of the access method in meeting key needs.';
  }
  if (ratingsMade && !someNeedsMet){
    secondPickString = 'This was based on Test-drive results.'
  }
  if (!ratingsMade && !someNeedsMet){
    secondPickString = 'Note, however, that we have not recorded ratings or met needs for this access method.';
  }

  return(
    <Row className="aa-review-row border-top">
      <p><strong>{heading}</strong>{spacer}
        <UserTextOrNone userText={getLabelForMethod(pick)} />
        {periodSpace}
        {firstPickString}{spacer}{secondPickString}
      </p>
      {someNeedsMet ?
        <ReportPickNeedsMet needsMet={needsMet} comments={needsMetComments}/>
        : <span></span>
      }
    </Row>
  );
}

function ReportPickNeedsMet(props){
  const needsMet = props.needsMet;
  const comments = props.comments;

  //extract the all available values from the checklist array, into array of strings
  const availableNeeds = CHECKLIST_BUTTONS.map(item => item.value);
  const unmetNeeds = diff(availableNeeds, needsMet);

  return(
    <>
    <div>
    <span className="aa-success fa fa-check-circle">&nbsp;</span>These needs are fully met:
      <ValuesList values={needsMet} lookup={CHECKLIST_BUTTONS} skipWarning={true} />
    </div>
    {(unmetNeeds.length > 0) &&
    <div>
    <span className="aa-warning fa fa-times-circle">&nbsp;</span>These needs are not fully met:
      <ValuesList values={unmetNeeds} lookup={CHECKLIST_BUTTONS} skipWarning={true} />
    </div>
    }
    {comments && <p>Comments: {comments}</p>}
    </>
  )
}
function diff(A, B) {
  return A.filter(function (a) {
      return B.indexOf(a) === -1;
  });
}

function ReportTestDriveDetails(props){
  const td = props.td;
  const index = props.index+1;

  let heading = `Details for Test-drive ${index}.`;
  let spacer = " ";

  let issueBtns = extractIssueButtons();

  return(
    <Row className="aa-review-row border-top">
      <Col>
        <Row>
          <Col>
            <strong>{heading}</strong>{spacer}
            <UserTextOrNone userText={getLabelForMethod(td)} />
          </Col>
        </Row>
        <TestDriveTableRow
          setupName={td.setupName}
          name={td.accessMethodName}
          starRating={td.clientRating}
          successRating={td.practitionerRating}
          showDetailsCol={true}
          border='none'
        />
        <Row className="aa-review-row">
          <Col>
          {( td.activities && td.activities.length > 0)
            ? <>
              {td.activities.map((a, ix) =>
                <ReportTestDriveActivity key={ix} a={a} index={ix}/>
              )}
              </>
          : <p>There were no activities in this Test-drive.</p>
          }
          </Col>
        </Row>
        {(td.issuesNoted && td.issuesNoted.length > 0) &&
          <div>
            We noted the following challenges during this test drive:
            <ValuesList values={td.issuesNoted} lookup={issueBtns} skipWarning={true} />
          </div> 
        }
        {td.practitionerComments &&
          <p>{td.practitionerComments}</p>
        }     
      </Col>
    </Row>
  )
}

function ReportTestDriveActivity(props){
  const a = props.a;
  const index = props.index+1;

  let heading = `Activity ${index}.`;
  let spacer = " ";
  let periodSpace = ". ";

  return(
    <>
    <p>
      <strong>{heading}</strong>{spacer}
      <UserTextOrNone userText={a.name} />
      {periodSpace}
      {a.type === TEST_DRIVE_ACTIVITIES.OWN_ACTIVITY
        ? <span>This is an activity that we chose.</span>
        : <span>This is an in-app activity that measures speed and accuracy during the task.</span>
      }
    </p>
    <p>
      Observations: {spacer}
      <UserTextOrNone userText={a.notes}/>
    </p>
    {a.type !== TEST_DRIVE_ACTIVITIES.OWN_ACTIVITY
      ? <ActivityMeasurements activity={a}/>
      : <></>
    }
    </>
  );
}

function ActivityMeasurements(props){
  const a = props.activity;

  let heading = 'Measurements: ';
  let validType = true;
  let speedString = 'Speed: ';
  let accuracyString = 'Accuracy: ';
  let naString = 'not available';

  switch(a.type){
    case( TEST_DRIVE_ACTIVITIES.TEXT_ENTRY_BASIC):
      if(a.wpm){
        speedString = speedString + a.wpm + " wpm";
      }
      else speedString = speedString + naString;
      if(a.accuracy){
        accuracyString = accuracyString + a.accuracy + " %";
      }  
      else accuracyString = accuracyString + naString;    
      break;
    case( TEST_DRIVE_ACTIVITIES.POINTING_BASIC):
      if(a.targetsPerMinute){
        speedString = speedString + a.targetsPerMinute + " targets/min";
      }
      else speedString = speedString + naString;
      if(a.accuracy){
        accuracyString = accuracyString + a.accuracy + " %";
      }
      else accuracyString = accuracyString + naString;
      break;
    case( TEST_DRIVE_ACTIVITIES.SWITCH_BASIC):
      if(a.duration){
        speedString = speedString + a.duration + " sec/hit";
      }
      else speedString = speedString + naString;
      if(a.extraActivations){
        accuracyString = accuracyString + a.extraActivations + " extra hits";
      }
      else accuracyString = accuracyString + naString;
      break;
    default:
      validType = false;
  }
  return(
    <>
    { validType ?
      <div>
        {heading}
        <ul className="aa-values-list list-unstyled">
          <li>{speedString}</li>
          <li>{accuracyString}</li>
        </ul>
      </div>
    : <span></span>
    }
    </>
  );
}
function ReportHeader(props){
  const {
    firstName,
    providerName,
  } = props;

  return(
    <>
    <div>Date: {getCurrentDate()}</div>
    <div>Provider: {providerName}</div>
    <div>Client: {firstName}</div>
    <br/>
    </>
  )
}
function getCurrentDate(){
  let separator = '/';
  let newDate = new Date()
  let date = newDate.getDate();
  let month = newDate.getMonth() + 1;
  let year = newDate.getFullYear();

  // return `${year}${separator}${month<10?`0${month}`:`${month}`}${separator}${date}`
  return `${month<10?`0${month}`:`${month}`}${separator}${date}${separator}${year}`
}

function ReportSummary(props){
  const {
    firstName,
    reportData
   } = props;

  //create Arrays of the relevant variables (each must be a string or array)
  const backgroundData = [
    reportData.activities, reportData.priority, reportData.platforms,
    reportData.environments, reportData.bodyPositions, reportData.indepSetup, reportData.indepUse,
    reportData.hasCurrentMethod, reportData.currentMethods, reportData.pastMethods, reportData.platformsUsed
  ];
  const abilitiesData = [
    reportData.bodySites, reportData.bodySiteComments,
    reportData.wearsGlasses, reportData.betterWithGlasses, reportData.hasOtherVisualAids, reportData.visualAidsNotes, 
      reportData.formalEvalNotes, reportData.visionIssues, reportData.issuesNotes,
    reportData.strenghts, reportData.limitations, reportData.otherIssues
  ];
  //then check if the Array packet has at least one truthy value
  const hasBackgroundInfo = backgroundData.some(hasAValue);
  const hasAbilitiesInfo = abilitiesData.some(hasAValue);
  const hasTestDrives = reportData.completedTestDrives.length > 0;
  const hasPicks = reportData.picks.length > 0;
  //TODO is that what we want? If you've done *anything* in the session, you should have or will be entering next steps.
  //this is an indirect check, but a direct check of nextSteps already entered might be misleading: could be empty but you are going
  //to add a note to the recommendations.  Hence the indirect check.
  const hasNextSteps = hasBackgroundInfo || hasAbilitiesInfo || hasTestDrives || hasPicks;
  const hasAnyDataAtAll = hasBackgroundInfo || hasAbilitiesInfo || hasTestDrives || hasPicks || hasNextSteps; //yes I know it's logically redundant with hasNextSteps

  return(
    <>
    { hasAnyDataAtAll ?
      <div>
        <p>In this assessment, we have:</p>
        <ul>
          {hasBackgroundInfo ?
            <li>Gathered background info</li>
            : <></>
          }
          {hasAbilitiesInfo ?
            <li>Evaluated abilities</li>
            : <></>
          }
          {hasTestDrives ?
            <li>Completed {reportData.completedTestDrives.length} Test Drive
              {reportData.completedTestDrives.length > 1 ? <>s</> : null}
            </li>
            : <></>
          }
          {hasPicks ?
            <li>Selected {reportData.picks.length} Pick
              {reportData.picks.length > 1 ? <>s</> : null} for further consideration
            </li>
            : <></>
          }
          {hasNextSteps ?
            <li>Recommended next steps to work toward {firstName}'s access needs</li>
            : <></>
          }
        </ul>
      <p>See the sections below for details.</p>
      </div>
      : <span>There aren't data available to show much in the report. The sections below show what is available.</span>

    }
    </>
  );
}

function ReportSectionHeader(props){
  const {
    headerText
  } = props;

  return(
    <div className="row aa-review-row border-top">
      <div className="col">
        <h4>{headerText}</h4>
      </div>
    </div>
  );
}
function ReportGoalsAndNeeds(props){
  const {
    activities,
    priority,
    platforms
  } = props;

  const activityBtns = extractActivityButtons();

  return(
    <div className="row aa-review-row">
      <div className="col-md-1">
        <span className="fa fa-bullseye fa-3x"></span>
      </div>
      <div className="col">
        <h5>Goals & Needs</h5>
        <div className="col-md-1"></div>
        <div className="col">
          <ReviewRow 
            border="none"
            padded="none"
            heading="Activities" 
            values={activities}
            lookup={activityBtns}
            skipWarning={true}
          />
          <ReviewRow 
            border="none"
            padded="none"
            heading="Priority activity" 
            values={priority}
            lookup={activityBtns}
            skipWarning={true}
          />
          <ReviewRow 
            border="none"
            padded="none"
            heading="Platforms" 
            values={platforms}
            lookup={PLATFORM_BUTTONS}
            skipWarning={true}
          />
        </div>
      </div>
    </div>
  );
}
function ReportContexts(props){
  const {
    environments,
    bodyPositions,
    indepSetup,
    indepUse
  } = props;

  return(
    <div className="row aa-review-row border-top">
      <div className="col-md-1">
        <span className="fa fa-users fa-2x"></span>
      </div>
      <div className="col">
        <h5>Contexts</h5>
        <div className="col-md-1"></div>
        <div className="col">
          <ReviewRow 
            border="none"
            padded="none"
            heading="Environments" 
            values={environments}
            lookup={ENVIRONMENT_BUTTONS}
            skipWarning={true}
          />
          <ReviewRow 
            border="none"
            padded="none"
            heading="Positions" 
            values={bodyPositions}
            lookup={POSITION_BUTTONS}
            skipWarning={true}
          />
          <ReviewRowIndependence 
            border="none"
            padded="none"
            heading="Independence" 
            indepSetup={indepSetup}
            indepUse={indepUse}
            lookup={IMPORTANCE_BUTTONS}
            skipWarning={true}
          />
        </div>
      </div>
    </div>
  );
}
function ReportHistory(props){
  const {
    hasCurrentMethod,
    currentMethods,
    pastMethods,
    platformsUsed
  } = props;

  //extract the names from the method arrays, into array of strings
  const cmNames = currentMethods.map(method => method.name);
  const pmNames = pastMethods.map(method => method.name);
 
  const BINARY_LABELS = [ 
    {
      value: 'true',
      label: 'yes',
    },
    {
      value: 'false',
      label: 'no'
    }
  ];

  return(
    <div className="row aa-review-row border-top">
      <div className="col-md-1">
        <span className="fa fa-clock-o fa-3x"></span>
      </div>
      <div className="col">
        <h5>History of Tech Use</h5>
        <div className="col-md-1"></div>
        <div className="col">
          <ReviewRow 
            border="none"
            padded="none"
            heading="Has a Current Method" 
            values={hasCurrentMethod}
            lookup={BINARY_LABELS}
            skipWarning={true}
          />
          {(hasCurrentMethod==='true') ? 
          <ReviewRow 
            border="none"
            padded="none"
            heading="Current Methods" 
            values={cmNames}
            lookup={null}
            skipWarning={true}
          />
          : <span></span>}
          <ReviewRow 
            border="none"
            padded="none"
            heading="Past Methods" 
            values={pmNames}
            lookup={null}
            skipWarning={true}
          />
          <ReviewRow 
            border="none"
            padded="none"
            heading="Platforms Used" 
            values={platformsUsed}
            lookup={PLATFORM_BUTTONS}
            skipWarning={true}
          />
        </div>
      </div>
    </div>
  );
}
function ReportBodySites(props){
  const {
    bodySites,
    bodySiteComments
  } = props;

  let bodySiteBtns = extractBodySiteButtons();

  return(
    <div className="row aa-review-row">
      <div className="col-md-1">
        <span className="fa fa-child fa-3x"></span>
      </div>
      <div className="col">
        <h5>Physical</h5>
        <div className="col">
          <ReviewRow 
            border="none"
            padded="none"
            heading="Priority body sites" 
            values={bodySites}
            lookup={bodySiteBtns}
            skipWarning={true}
          />
          <ReviewRow 
            border="none"
            padded="none"
            heading="Notes on body sites" 
            values={bodySiteComments}
            lookup={null}
            skipWarning={true}
          />
        </div>
      </div>
    </div>
  );
}
function ReportVisualAbilities(props){
  const {
    wearsGlasses,
    betterWithGlasses,
    hasOtherVisualAids,
    visualAidsNotes,
    formalEvalNotes,
    visionIssues,
    issuesNotes
  } = props;

  const THREE_STATE_LABELS = [ 
    {
      value: 'true',
      label: 'yes',
    },
    {
      value: 'false',
      label: 'no'
    },
    {
      value: 'not-sure',
      label: 'not sure'
    }
  ];

  return(
    <div className="row aa-review-row border-top">
    <div className="col-md-1">
      <span className="fa fa-eye fa-2x"></span>
    </div>
    <div className="col">
      <h5>Visual</h5>
      <div className="col">
        <ReviewRow 
          border="none"
          padded="none"
          heading="Wears glasses: " 
          values={wearsGlasses}
          lookup={THREE_STATE_LABELS}
          skipWarning={true}
        />
        {(wearsGlasses==='true') ? 
        <ReviewRow 
          border="none"
          padded="none"
          heading="Sees screen better with glasses: " 
          values={betterWithGlasses}
          lookup={THREE_STATE_LABELS}
          skipWarning={true}
        />
        : <span></span>}
        <ReviewRow 
          border="none"
          padded="none"
          heading="Has other visual aids: " 
          values={hasOtherVisualAids}
          lookup={THREE_STATE_LABELS}
          skipWarning={true}
        />
        {(hasOtherVisualAids==='true') ? 
          <ReviewRow 
          border="none"
          padded="none"
          heading="List of other visual aids " 
          values={visualAidsNotes}
          lookup={null}
          skipWarning={true}
          />
        : <span></span>}
        <ReviewRow 
          border="none"
          padded="none"
          heading="Formal vision eval notes " 
          values={formalEvalNotes}
          lookup={null}
          skipWarning={true}
        />
        <ReviewRow 
          border="none"
          padded="none"
          heading="Vision issues" 
          values={visionIssues}
          lookup={VISION_BUTTONS}
          skipWarning={true}
        />
        <ReviewRow 
          border="none"
          padded="none"
          heading="Comments on vision issues" 
          values={issuesNotes}
          lookup={null}
          skipWarning={true}
        />
      </div>
    </div>
  </div>
  );
}
function ReportLanguageCognition(props){
  const {
    strengths,
    limitations
  } = props;

  return(
    <div className="row aa-review-row border-top">
      <div className="col-md-1">
        <span className="fa fa-puzzle-piece fa-2x"></span>
      </div>
      <div className="col">
        <h5>Language and Cognition</h5>
        <div className="col">
          <ReviewRow 
            border="none"
            padded="none"
            heading="Strengths" 
            values={strengths}
            lookup={null}
            skipWarning={true}
          />
          <ReviewRow 
            border="none"
            padded="none"
            heading="Limitations" 
            values={limitations}
            lookup={null}
            skipWarning={true}
          />
        </div>
      </div>
    </div>
  );
}
function ReportOtherIssues(props){
  const {
    otherIssues
  } = props;

  return(
    <div className="row aa-review-row border-top">
      <div className="col-md-1">
        <span className="fa fa-superpowers fa-2x"></span>
      </div>
      <div className="col">
        <h5>Other Characteristics</h5>
        <div className="col">
          <ReviewRow 
            border="none"
            padded="none"
            heading="Additional notes" 
            values={otherIssues}
            lookup={null}
            skipWarning={true}
          />
        </div>
      </div>
    </div>
  );
}
//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function ReviewTabNextstepsReport(props) {
  const {
    firstName,
    pronouns,
    providerName,
    reportData,
    summaryNotes,
    onChangeSummaryNotes,
    resetSummaryNotes,
    backgroundNotes,
    onChangeBackgroundNotes,
    resetBackgroundNotes,
    abilitiesNotes,
    onChangeAbilitiesNotes,
    resetAbilitiesNotes,
    testdrivesNotes,
    onChangeTestdrivesNotes,
    resetTestdrivesNotes,
    decisionsNotes,
    onChangeDecisionsNotes,
    resetDecisionsNotes,
    recommendationsNotes,
    onChangeRecommendationsNotes,
    resetRecommendationsNotes,
    saveFormData,
    onContinue
  } = props;

  const pageData = {
    image: "./assets/images/target-icon.jpg",
    faIcon: "",
    title: `Session Report`,
    subTitle: ``,
    learnMore: {linkText: "Learn More about what you can do with reports, including editing and printing",
                title: "Reporting",
                content: REPORT_HELP
                },
    nextRoute: "", 
    sidebar: false
  }

  console.log("Rendering ReviewTabNextstepsReport, data: " + JSON.stringify(reportData));
  
  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <ReviewTabNextstepsReportContent 
          firstName={firstName}
          providerName={providerName}
          reportData={reportData}
          summaryNotes={summaryNotes}
          onChangeSummaryNotes={onChangeSummaryNotes}
          resetSummaryNotes={resetSummaryNotes}
          backgroundNotes={backgroundNotes}
          onChangeBackgroundNotes={onChangeBackgroundNotes}
          resetBackgroundNotes={resetBackgroundNotes}
          abilitiesNotes={abilitiesNotes}
          onChangeAbilitiesNotes={onChangeAbilitiesNotes}
          resetAbilitiesNotes={resetAbilitiesNotes}
          testdrivesNotes={testdrivesNotes}
          onChangeTestdrivesNotes={onChangeTestdrivesNotes}
          resetTestdrivesNotes={resetTestdrivesNotes}
          decisionsNotes={decisionsNotes}
          onChangeDecisionsNotes={onChangeDecisionsNotes}
          resetDecisionsNotes={resetDecisionsNotes}
          recommendationsNotes={recommendationsNotes}
          onChangeRecommendationsNotes={onChangeRecommendationsNotes}
          resetRecommendationsNotes={resetRecommendationsNotes}
          saveFormData={saveFormData}
      />} 
      onContinue={onContinue} 
    />
    </>
  );
}

ReviewTabNextstepsReport.propTypes = propTypes;
ReviewTabNextstepsReport.defaultProps = defaultProps;

export default ReviewTabNextstepsReport;